import React, { useState, useRef, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../../config';
import { useOrganization } from '../../../context/OrganizationContext';

import {
  ArrowCounterClockwise,
  CaretDown,
  CaretUp,
  CheckCircle,
  CircleNotch,
  FileText,
  MagicWand,
  Microphone,
  MicrophoneSlash,
  Pause,
  Play,
  UploadSimple,
  X
} from '@phosphor-icons/react';
/**
 * TestInputPanel - Compact input panel for agent testing
 * Uses percentage-based heights to ensure proper space allocation
 */

const INPUT_TYPE_CONFIG = {
  text: { icon: FileText, label: 'Text', accept: null },
  file_pdf: { icon: FileText, label: 'PDF', accept: '.pdf' },
  file_doc: { icon: FileText, label: 'Document', accept: '.doc,.docx' },
  file_audio: { icon: Microphone, label: 'Audio', accept: '.mp3,.wav,.m4a,.ogg,.webm' },
  url: { icon: FileText, label: 'URL', accept: null },
  audio: { icon: Microphone, label: 'Audio', accept: '.mp3,.wav,.m4a' }
};

const TestInputPanel = ({ 
  agentConfig, 
  agentId,
  onRunTest,
  isRunning = false,
  isCollapsed = false,
  onToggleCollapse,
  initialInput = null
}) => {
  const { activeOrganization } = useOrganization();
  
  const [textInput, setTextInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Handle pre-filled input from replay
  useEffect(() => {
    if (initialInput) {
      if (typeof initialInput === 'string') {
        setTextInput(initialInput);
      } else if (typeof initialInput === 'object') {
        if (initialInput.textInput) setTextInput(initialInput.textInput);
        if (initialInput.urlInput) setUrlInput(initialInput.urlInput);
      }
    }
  }, [initialInput]);

  const acceptedInputs = useMemo(() => agentConfig?.accepted_inputs || ['text'], [agentConfig?.accepted_inputs]);
  const hasFileInputs = useMemo(() => acceptedInputs.some(t => t.startsWith('file_') || t === 'image' || t === 'audio'), [acceptedInputs]);
  const hasUrlInput = acceptedInputs.includes('url');
  const hasAudioInput = acceptedInputs.some(t => t === 'file_audio' || t === 'audio');
  const hasTextInput = acceptedInputs.includes('text');

  const acceptString = useMemo(() => {
    return acceptedInputs.map(t => INPUT_TYPE_CONFIG[t]?.accept).filter(Boolean).join(',') || '*';
  }, [acceptedInputs]);

  const handleFileSelect = (e) => {
    setUploadedFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current.onstop = () => {
        setAudioBlob(new Blob(chunksRef.current, { type: 'audio/webm' }));
        stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const generateTestScenario = async () => {
    setIsGenerating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/agents/generate-test-scenario`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': activeOrganization?.organization_id
        },
        body: JSON.stringify({
          agent_config: {
            name: agentConfig?.name,
            description: agentConfig?.description || agentConfig?.short_description,
            capabilities: agentConfig?.required_capabilities,
          },
          input_types: acceptedInputs
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.text_input) setTextInput(data.text_input);
        if (data.url_input) setUrlInput(data.url_input);
        toast.success('Test scenario generated!');
      } else {
        throw new Error('API error');
      }
    } catch (error) {
      // Fallback
      const name = agentConfig?.name?.toLowerCase() || '';
      if (name.includes('meeting') || name.includes('notes')) {
        setTextInput(`Meeting Notes - Q4 Planning\nDate: January 15, 2025\nAttendees: Sarah, John, Mike\n\nAction Items:\n- Sarah to send roadmap\n- John to estimate effort\n- Mike to prepare mockups`);
      } else {
        setTextInput(`Test input for ${agentConfig?.name || 'agent'}.\nPlease process this request.`);
      }
      toast.success('Sample loaded');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRun = () => {
    if (!textInput.trim() && uploadedFiles.length === 0 && !urlInput && !audioBlob) {
      toast.error('Please provide some input');
      return;
    }
    onRunTest?.({ textInput, urlInput, files: uploadedFiles, audioBlob });
  };

  if (isCollapsed) {
    return (
      <div 
        onClick={onToggleCollapse}
        className="h-full flex items-center justify-between px-3 py-2 bg-[#0d0e12] cursor-pointer hover:bg-slate-800/50"
      >
        <div className="flex items-center gap-2">
          <Play size={14} className="text-indigo-400" />
          <span className="text-xs font-medium text-white">Test Agent</span>
        </div>
        <CaretUp size={14} className="text-slate-500" />
      </div>
    );
  }

  return (
    <div 
      className="h-full bg-[#0d0e12] flex flex-col"
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Header - Fixed height */}
      <div 
        onClick={onToggleCollapse}
        className="flex items-center justify-between px-3 py-2 border-b border-slate-800 cursor-pointer hover:bg-slate-800/30"
        style={{ flexShrink: 0 }}
      >
        <div className="flex items-center gap-2">
          <Play size={14} className="text-indigo-400" />
          <span className="text-xs font-medium text-white">Test Agent</span>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-500/20 text-amber-400">BETA</span>
        </div>
        <CaretDown size={14} className="text-slate-500" />
      </div>

      {/* Content area - MUST GROW */}
      <div 
        className="p-3 flex flex-col gap-2 overflow-hidden"
        style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {/* Optional file/audio inputs */}
        {hasFileInputs && (
          <div style={{ flexShrink: 0 }}>
            <input ref={fileInputRef} type="file" multiple accept={acceptString} onChange={handleFileSelect} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg border border-dashed border-slate-700 text-slate-400 text-xs hover:border-indigo-500/50"
            >
              <UploadSimple size={12} /> UploadSimple Files
            </button>
            {uploadedFiles.length > 0 && (
              <div className="mt-1 space-y-1">
                {uploadedFiles.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 px-2 py-1 rounded bg-slate-800/50 text-xs">
                    <FileText size={10} className="text-slate-500" />
                    <span className="flex-1 truncate text-slate-300">{file.name}</span>
                    <button onClick={() => removeFile(i)} className="text-slate-500 hover:text-red-400"><X size={10} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {hasAudioInput && (
          <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
            {!isRecording && !audioBlob && (
              <button onClick={startRecording} className="flex-1 flex items-center justify-center gap-2 py-1.5 rounded-lg bg-purple-500/10 text-purple-400 text-xs">
                <Microphone size={12} /> Record
              </button>
            )}
            {isRecording && (
              <button onClick={stopRecording} className="flex-1 flex items-center justify-center gap-2 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs animate-pulse">
                <Pause size={12} /> Stop
              </button>
            )}
            {audioBlob && !isRecording && (
              <>
                <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle size={10} /> Ready</span>
                <button onClick={() => setAudioBlob(null)} className="text-slate-500 hover:text-white"><ArrowCounterClockwise size={10} /></button>
              </>
            )}
          </div>
        )}

        {hasUrlInput && (
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com"
            className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-xs placeholder-slate-500 focus:outline-none"
            style={{ flexShrink: 0 }}
          />
        )}

        {/* TEXT AREA - THIS MUST EXPAND */}
        {hasTextInput && (
          <div style={{ flex: '1 1 auto', minHeight: '100px', display: 'flex', flexDirection: 'column' }}>
            <textarea
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Enter test input or click 'Generate Test Scenario'..."
              className="w-full px-3 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/50 resize-none"
              style={{ flex: '1 1 auto', minHeight: '100px', height: '100%' }}
            />
          </div>
        )}

        {/* Buttons - Fixed at bottom */}
        <div style={{ flexShrink: 0 }} className="space-y-2 pt-1">
          <button
            onClick={generateTestScenario}
            disabled={isGenerating}
            className="w-full flex items-center justify-center gap-2 py-1.5 rounded-lg border border-dashed border-slate-700 text-slate-500 text-xs hover:border-amber-500/50 hover:text-amber-400 disabled:opacity-50"
          >
            {isGenerating ? <CircleNotch size={12} className="animate-spin" /> : <MagicWand size={12} />}
            Generate Test Scenario
          </button>

          <button
            onClick={handleRun}
            disabled={isRunning || (!textInput.trim() && uploadedFiles.length === 0 && !urlInput && !audioBlob)}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-medium disabled:opacity-50 shadow-lg shadow-indigo-500/20"
          >
            {isRunning ? <><CircleNotch size={16} className="animate-spin" /> Running...</> : <><Play size={16} /> Run Test</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestInputPanel;
